import type { ContextType } from '@/stores/orgContextStore';

const CHANNEL_NAME = 'jointo-org-context';
export const ORG_CONTEXT_STORAGE_EVENT_KEY = 'jointo:org-context-event';
const TAB_ID_SESSION_KEY = 'jointo:org-context-tab-id';
const MAX_SEEN_EVENT_IDS = 100;
const VALID_CONTEXT_TYPES = new Set<ContextType>([0, 1, 2]);
let fallbackTabId: string | null = null;

export interface OrgContextChangedEvent {
  type: 'org-context-changed';
  eventId: string;
  sourceTabId: string;
  targetOrgId: string | null;
  targetContext: ContextType;
  targetLabel: string;
  timestamp: number;
}

interface CreateOrgContextChangedEventInput {
  sourceTabId: string;
  targetOrgId: string | null;
  targetContext: ContextType;
  targetLabel: string;
  now?: number;
  eventId?: string;
}

interface StorageEventLike {
  key: string | null;
  newValue: string | null;
}

function createId(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getFallbackTabId(): string {
  fallbackTabId ??= createId('tab');
  return fallbackTabId;
}

export function getOrgContextTabId(): string {
  if (typeof sessionStorage === 'undefined') {
    return getFallbackTabId();
  }

  try {
    const existing = sessionStorage.getItem(TAB_ID_SESSION_KEY);
    if (existing) return existing;

    const tabId = createId('tab');
    sessionStorage.setItem(TAB_ID_SESSION_KEY, tabId);
    return tabId;
  } catch {
    return getFallbackTabId();
  }
}

export function createOrgContextChangedEvent({
  sourceTabId,
  targetOrgId,
  targetContext,
  targetLabel,
  now = Date.now(),
  eventId = createId('orgctx'),
}: CreateOrgContextChangedEventInput): OrgContextChangedEvent {
  return {
    type: 'org-context-changed',
    eventId,
    sourceTabId,
    targetOrgId,
    targetContext,
    targetLabel,
    timestamp: now,
  };
}

function isOrgContextChangedEvent(value: unknown): value is OrgContextChangedEvent {
  if (!value || typeof value !== 'object') return false;
  const event = value as Partial<OrgContextChangedEvent>;
  return (
    event.type === 'org-context-changed' &&
    typeof event.eventId === 'string' &&
    typeof event.sourceTabId === 'string' &&
    (typeof event.targetOrgId === 'string' || event.targetOrgId === null) &&
    VALID_CONTEXT_TYPES.has(event.targetContext as ContextType) &&
    typeof event.targetLabel === 'string' &&
    typeof event.timestamp === 'number'
  );
}

export function parseOrgContextStorageEvent(
  event: StorageEventLike
): OrgContextChangedEvent | null {
  if (event.key !== ORG_CONTEXT_STORAGE_EVENT_KEY || !event.newValue) return null;
  try {
    const parsed: unknown = JSON.parse(event.newValue);
    return isOrgContextChangedEvent(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function shouldHandleOrgContextEvent(
  event: OrgContextChangedEvent,
  currentTabId: string,
  seenEventIds: Set<string>
): boolean {
  if (event.sourceTabId === currentTabId) return false;
  if (seenEventIds.has(event.eventId)) return false;
  seenEventIds.add(event.eventId);
  while (seenEventIds.size > MAX_SEEN_EVENT_IDS) {
    const oldestEventId = seenEventIds.values().next().value;
    if (!oldestEventId) break;
    seenEventIds.delete(oldestEventId);
  }
  return true;
}

export function publishOrgContextChangedEvent(event: OrgContextChangedEvent): void {
  if (typeof BroadcastChannel !== 'undefined') {
    try {
      const channel = new BroadcastChannel(CHANNEL_NAME);
      channel.postMessage(event);
      channel.close();
    } catch {
      // BroadcastChannel may be unavailable in restricted WebViews; storage event remains as fallback.
    }
  }

  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(ORG_CONTEXT_STORAGE_EVENT_KEY, JSON.stringify(event));
    } catch {
      // Ignore storage failures; current tab has already switched context.
    }
  }
}

export function subscribeOrgContextChangedEvents(
  handler: (event: OrgContextChangedEvent) => void
): () => void {
  const currentTabId = getOrgContextTabId();
  const seenEventIds = new Set<string>();
  let channel: BroadcastChannel | null = null;

  const handleEvent = (event: OrgContextChangedEvent) => {
    if (shouldHandleOrgContextEvent(event, currentTabId, seenEventIds)) {
      handler(event);
    }
  };

  if (typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (messageEvent: MessageEvent<unknown>) => {
      if (isOrgContextChangedEvent(messageEvent.data)) {
        handleEvent(messageEvent.data);
      }
    };
  }

  const handleStorage = (storageEvent: StorageEvent) => {
    const event = parseOrgContextStorageEvent(storageEvent);
    if (event) handleEvent(event);
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('storage', handleStorage);
  }

  return () => {
    channel?.close();
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', handleStorage);
    }
  };
}
